import { KeyedArray } from "@repo/models";
import { createContext, ReactNode, useContext, useMemo } from "react";

import { sectionIsVisible } from "..";
import { FlattenedSection } from "../dynamicComponentContainer/types";
import { byOrdinal } from "../helpers/sectionMapping";
import { sectionType } from "../models/enums";
import { MappedSection } from "../models/mappedComponent";

export interface DynamicSectionContextType {
	sections: MappedSection[];
	sectionMap: KeyedArray<MappedSection>;
	visibleSections: MappedSection[];
	allSections: FlattenedSection[];
}

const defaultContextValues = {
	sections: [],
} as DynamicSectionContextType;

interface DynamicSectionProps {
	children: ReactNode;
	sections: MappedSection[];
	alwaysShowPages?: boolean;
}

export const DynamicSectionContext = createContext<DynamicSectionContextType>(defaultContextValues);
export const useDynamicSectionContext = () => useContext(DynamicSectionContext);
export function DynamicSectionProvider(props: DynamicSectionProps) {
	const { children, sections, alwaysShowPages } = props;
	const allSections = useMemo(
		() => getSections(sections, alwaysShowPages).filter(sectionIsVisible),
		[sections, alwaysShowPages],
	);
	const visibleSections = useMemo(() => sections.filter(sectionIsVisible).sort(byOrdinal), [sections]);
	const sectionMap = useMemo(() => new KeyedArray(sections ?? [], (s) => s.linkName), [sections]);
	return (
		<DynamicSectionContext.Provider
			value={{
				sections: allSections,
				sectionMap,
				visibleSections,
				allSections,
			}}
		>
			{children}
		</DynamicSectionContext.Provider>
	);
}

function getSections(sections: FlattenedSection[], alwaysShowPages?: boolean) {
	let flattenedSections = [] as FlattenedSection[];
	for (const section of sections.filter(sectionIsVisible)) {
		const isSummary = section.section.type === sectionType.summary;
		const isIteratedEditable = section.section.type === sectionType.iteratedEditable;

		// Don't show sections that only exist to hold child sections when always show is on
		if (alwaysShowPages && !isSummary && !isIteratedEditable) {
			if (section.components.length !== 0 || section.childSections.length === 0) {
				flattenedSections.push(section);
			}
		} else if (section.components.length || isSummary || isIteratedEditable) {
			flattenedSections.push(section);
		}
		// If iterated section, pass use index of a section.
		// Otherwise, just passing index through
		// Index is only useful for iterated sections,
		// so if under an iterated section, want to just keep using index of parent
		const useIndex = Array.isArray(section.propertyValue);
		section.childSections.forEach((s: FlattenedSection, i) => {
			s.index = useIndex ? i : section.index;
			s.parentPropertyName = section.propertyName;
		});

		let childSections = getSections(section.childSections);
		//If summary, we want children be on this page, not have an own section
		if (isSummary || isIteratedEditable) childSections = [];
		flattenedSections = [...flattenedSections, ...childSections];
	}
	return flattenedSections;
}
